/**
 * Per-clip sound changes for library loops: pitch (semitones), speed (half/double time) and
 * tempo stretch. Pure: callers commit the returned tracks with one pushState.
 */
import { isRenderedClip } from '@/audio/clipKinds';
import { SPEED_CHOICES, clipSpeed, sessionBarsToNative, timelineBars } from '@/audio/clipTiming';

export const PITCH_MIN = -12;
export const PITCH_MAX = 12;
const EPS = 1e-6;

const clampPitch = (value) => Math.max(PITCH_MIN, Math.min(PITCH_MAX, Math.round(Number(value) || 0)));

function soundPatch(clip, patch) {
  const next = { ...clip };
  if ('pitch' in patch || 'pitchDelta' in patch) {
    const pitch = clampPitch('pitch' in patch ? patch.pitch : (Number(clip.pitch) || 0) + Number(patch.pitchDelta || 0));
    if (pitch) next.pitch = pitch; else delete next.pitch;
  }
  if ('speed' in patch) {
    const speed = SPEED_CHOICES.includes(Number(patch.speed)) ? Number(patch.speed) : 1;
    if (speed !== 1) next.speed = speed; else delete next.speed;
  }
  if ('stretch' in patch) { if (patch.stretch === false) next.stretch = false; else delete next.stretch; }
  return next;
}

/** Half time doubles a clip; stop it at the next clip on its track instead of stacking on top. */
function fitBeforeNext(next, clip, siblings, bpm) {
  if (clipSpeed(next) >= clipSpeed(clip)) return next;
  const start = Number(next.start_bar || 0);
  const end = start + timelineBars(next, bpm);
  const nextStart = Math.min(Infinity, ...siblings
    .filter((other) => other.id !== clip.id && Number(other.start_bar || 0) > start + EPS)
    .map((other) => Number(other.start_bar || 0)));
  if (!(end > nextStart + EPS)) return next;
  return { ...next, bars: sessionBarsToNative(nextStart - start, next, bpm), isWholeLoop: false };
}

/**
 * Apply `patch` ({pitch} | {pitchDelta} | {speed} | {stretch}) to the library loops in `ids`.
 * Drum/keys patterns and Knock clips are left alone. Returns { tracks, changed }.
 */
export function applyClipSound(tracks, ids, patch, bpm) {
  let changed = false;
  const nextTracks = (tracks || []).map((track) => {
    const clips = track.clips || [];
    if (!clips.some((clip) => ids.has(clip.id) && !isRenderedClip(clip))) return track;
    return {
      ...track,
      clips: clips.map((clip) => {
        if (!ids.has(clip.id) || isRenderedClip(clip)) return clip;
        const next = fitBeforeNext(soundPatch(clip, patch), clip, clips, bpm);
        if (['pitch', 'speed', 'stretch', 'bars', 'isWholeLoop'].some((key) => next[key] !== clip[key])) changed = true;
        return next;
      }),
    };
  });
  return { tracks: changed ? nextTracks : tracks, changed };
}
